import CustomTooltip from '@gui/components/generic/CustomTooltip';
import { Box, Divider, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import type { ReactNode } from 'react';

export type MetricRow = {
    label: string;
    value: ReactNode;
    unit?: string;
    wrapUnit?: boolean;
    highlight?: boolean;
    wrap?: boolean;
    hideUnit?: boolean;
    tooltip?: string;
    dividerAfter?: boolean;
};

type MetricTableProps = {
    rows: MetricRow[];
    controls?: ReactNode | null;
    trailingRows?: MetricRow[];
    title?: string;
    showHeader?: boolean;
};

export const MetricTable = ({ rows, controls = null, trailingRows = [], title, showHeader = true }: MetricTableProps) => (
    <Box key={title ?? 'metrics-table'} display="flex" flexDirection="column" gap={0}>
        <Table
            size="small"
            sx={{
                borderCollapse: 'collapse',
                tableLayout: 'fixed',
                width: '100%',
                '& .MuiTableCell-root': {
                    borderBottom: 'none',
                    py: 0.5,
                    px: 0.5,
                },
                '& .MuiTableHead-root .MuiTableCell-root': {
                    color: 'text.primary',
                    fontWeight: 700,
                    pt: 0,
                    pb: 0,
                    borderBottom: '2px solid',
                    borderColor: 'divider',
                },
                '& .MuiTableHead-root + .MuiTableBody-root tr:first-of-type td': {
                    borderTop: '2px solid',
                    borderColor: 'divider',
                },
                '& .metric-label': {
                    color: 'text.primary',
                    lineHeight: 1.2,
                    whiteSpace: 'normal',
                    wordBreak: 'break-word',
                    hyphens: 'auto',
                    fontWeight: 400,
                    textAlign: 'left',
                },
                '& .metric-value': {
                    whiteSpace: 'nowrap',
                    color: 'text.primary',
                    fontWeight: 400,
                    textAlign: 'right',
                    paddingRight: 0.25,
                },
                '& .metric-value.metric-wrap': {
                    whiteSpace: 'normal',
                    wordBreak: 'break-word',
                    hyphens: 'auto',
                },
                '& .metric-unit': {
                    textAlign: 'right',
                    color: 'text.primary',
                    fontWeight: 400,
                    paddingRight: 0,
                },
            }}
        >
            <colgroup>
                <col style={{ width: '54%' }} />
                <col style={{ width: '20%' }} />
                <col style={{ width: '26%' }} />
            </colgroup>
            {showHeader && (
                <TableHead>
                    <TableRow>
                        <TableCell align="left">Metric</TableCell>
                        <TableCell align="right">Value</TableCell>
                        <TableCell align="right" sx={{ pr: 0 }}>
                            Unit
                        </TableCell>
                    </TableRow>
                </TableHead>
            )}
            <TableBody>
                {rows.flatMap((row, idx) => {
                    const contentRow = (
                        <TableRow key={`${row.label}-${idx}`}>
                            <TableCell className="metric-label">
                                {row.tooltip ? (
                                    <CustomTooltip mode="hover" side="right" title={row.tooltip}>
                                        <Box component="span" sx={{ cursor: 'help' }}>
                                            {row.label}
                                        </Box>
                                    </CustomTooltip>
                                ) : (
                                    row.label
                                )}
                            </TableCell>
                            {row.hideUnit ? (
                                <TableCell className="metric-value metric-wrap" align="left" colSpan={2}>
                                    {row.value}
                                </TableCell>
                            ) : (
                                <>
                                    <TableCell className={`metric-value ${row.wrap ? 'metric-wrap' : ''}`} align="right">
                                        {row.value}
                                    </TableCell>
                                    <TableCell className="metric-unit" align="right">
                                        {row.unit != '' ? (row.wrapUnit === false ? row.unit : '[' + row.unit + ']') : ''}
                                    </TableCell>
                                </>
                            )}
                        </TableRow>
                    );

                    if (!row.dividerAfter) return [contentRow];

                    return [
                        contentRow,
                        <TableRow key={`${row.label}-${idx}-divider`}>
                            <TableCell colSpan={3} sx={{ p: 0, pt: 0.25, pb: 0.5 }}>
                                <Divider sx={{ borderColor: 'rgba(0, 0, 0, 0.22)' }} />
                            </TableCell>
                        </TableRow>,
                    ];
                })}
            </TableBody>
        </Table>
        {controls && (
            <Box
                sx={{
                    borderBottom: trailingRows.length > 0 ? 1 : 0,
                    borderColor: 'rgba(0, 0, 0, 0.22)',
                    pb: trailingRows.length > 0 ? 0.5 : 0,
                }}
            >
                {controls}
            </Box>
        )}
        {trailingRows.length > 0 && (
            <Table size="small" className="performance-metrics-trailing-table">
                <colgroup>
                    <col style={{ width: '54%' }} />
                    <col style={{ width: '20%' }} />
                    <col style={{ width: '26%' }} />
                </colgroup>
                <TableBody>
                    {trailingRows.map((row, idx) => (
                        <TableRow key={`${row.label}-trailing-${idx}`}>
                            <TableCell className="metric-label">
                                {row.tooltip ? (
                                    <CustomTooltip mode="hover" side="right" title={row.tooltip}>
                                        <Box component="span" sx={{ cursor: 'help' }}>
                                            {row.label}
                                        </Box>
                                    </CustomTooltip>
                                ) : (
                                    row.label
                                )}
                            </TableCell>

                            {row.hideUnit ? (
                                <TableCell className="metric-value metric-wrap" align="left" colSpan={2}>
                                    {row.value}
                                </TableCell>
                            ) : (
                                <>
                                    <TableCell className={`metric-value ${row.wrap ? 'metric-wrap' : ''}`} align="right">
                                        {row.value}
                                    </TableCell>
                                    <TableCell className="metric-unit" align="right">
                                        {row.unit != '' ? (row.wrapUnit === false ? row.unit : '[' + row.unit + ']') : ''}
                                    </TableCell>
                                </>
                            )}
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        )}
    </Box>
);
